import { useCallback, useEffect, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { OneModal } from '@factorialco/factorial-one-react/dist/experimental'
import { AutoGrid } from '@factorialco/factorial-one-react/dist/experimental'
import { Bell, Image, Megaphone, Phone, Video } from '@factorialco/factorial-one-react/icons/app'
import { useLocalParticipant, useMediaDeviceSelect } from '@livekit/components-react'

import i18n from 'modules/core/lib/i18n'

import { MediaDeviceSelect } from 'modules/meetings/components/MediaDeviceSelect'
import {
  VIRTUAL_BACKGROUND_TYPES,
  getVirtualBackgroundOptions,
} from 'modules/meetings/lib/constants/VirtualBackground'
import { useRoomContext } from 'modules/meetings/lib/hooks/useRoomContext'
import { useVirtualBackgroundSettings } from 'modules/meetings/lib/hooks/useVirtualBackgroundSettings'
import {
  applyVirtualBackground,
  isVirtualBackgroundSupported,
} from 'modules/meetings/lib/utils/virtualBackgroundUtils'

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
  audioEnabled: boolean
  videoEnabled: boolean
  setAudioEnabled: (enabled: boolean) => void
  setVideoEnabled: (enabled: boolean) => void
}

export const SettingsModal = ({
  isOpen,
  onClose,
  audioEnabled,
  videoEnabled,
  setAudioEnabled,
  setVideoEnabled,
}: SettingsModalProps) => {
  const { soundsEnabled, setSoundsEnabled } = useRoomContext()
  const { localParticipant } = useLocalParticipant()
  const [backgroundSupported, setBackgroundSupported] = useState(false)
  const { backgroundType, setBackgroundType } = useVirtualBackgroundSettings()
  const [activeTab, setActiveTab] = useState<string>('general')

  const {
    devices: audioInputDevices,
    activeDeviceId: activeAudioInputId,
    setActiveMediaDevice: setActiveAudioDevice,
  } = useMediaDeviceSelect({ kind: 'audioinput' })

  const {
    devices: videoInputDevices,
    activeDeviceId: activeVideoInputId,
    setActiveMediaDevice: setActiveVideoDevice,
  } = useMediaDeviceSelect({ kind: 'videoinput' })

  const {
    devices: audioOutputDevices,
    activeDeviceId: activeAudioOutputId,
    setActiveMediaDevice: setActiveAudioOutputDevice,
  } = useMediaDeviceSelect({ kind: 'audiooutput' })

  const isAudioOutputSelectionSupported = 'setSinkId' in HTMLMediaElement.prototype

  // Load saved audio output device from localStorage on mount
  useEffect(() => {
    const savedAudioOutputDeviceId = localStorage.getItem('livekit-audio-output-device')
    if (savedAudioOutputDeviceId && setActiveAudioOutputDevice) {
      setActiveAudioOutputDevice(savedAudioOutputDeviceId)
    }
  }, [setActiveAudioOutputDevice])

  // Check blur support on mount
  useEffect(() => {
    isVirtualBackgroundSupported().then((supported) => setBackgroundSupported(supported))
  }, [])

  const handleAudioInputChange = useCallback(
    async (deviceId: string) => {
      try {
        if (setActiveAudioDevice) {
          await setActiveAudioDevice(deviceId)
          if (!audioEnabled) {
            await localParticipant.setMicrophoneEnabled(true)
            setAudioEnabled(true)
          }
        }
      } catch (error) {}
    },
    [localParticipant, setActiveAudioDevice, audioEnabled, setAudioEnabled]
  )

  const handleVideoInputChange = useCallback(
    async (deviceId: string) => {
      try {
        if (setActiveVideoDevice) {
          await setActiveVideoDevice(deviceId)
          if (!videoEnabled) {
            await localParticipant.setCameraEnabled(true)
            setVideoEnabled(true)
          }
        }
      } catch (error) {}
    },
    [localParticipant, setActiveVideoDevice, videoEnabled, setVideoEnabled]
  )

  const handleAudioOutputChange = useCallback(
    async (deviceId: string) => {
      if (isAudioOutputSelectionSupported && setActiveAudioOutputDevice) {
        await setActiveAudioOutputDevice(deviceId)
        // Save to localStorage for persistence across sessions
        localStorage.setItem('livekit-audio-output-device', deviceId)
      }
    },
    [isAudioOutputSelectionSupported, setActiveAudioOutputDevice]
  )

  const handleSoundsChange = useCallback(
    (value: string) => {
      setSoundsEnabled(value === 'true')
    },
    [setSoundsEnabled]
  )

  const handleBackgroundChange = useCallback(
    async (value: string) => {
      const success = await applyVirtualBackground(localParticipant, value)
      if (success) {
        setBackgroundType(value)
      }
    },
    [localParticipant, setBackgroundType]
  )

  return (
    <OneModal isOpen={isOpen} onClose={onClose}>
      <OneModal.Header title={i18n.t('meetings.room.settings.title')} />
      <OneModal.Content
        activeTabId={activeTab}
        setActiveTabId={(tabId) => {
          setActiveTab(tabId)
        }}
        tabs={[
          {
            id: 'general',
            label: i18n.t('meetings.room.settings.general_title'),
          },
          {
            id: 'effects',
            label: i18n.t('meetings.room.settings.effects_title'),
          },
        ]}
      >
        <Box flexDirection="column" padding="s20">
          <AutoGrid gap="4" tileSize="sm">
            {activeTab === 'general' && (
              <>
                <Box flexDirection="column" gap="s4">
                  <Text.Normal size="200" weight="medium">
                    {i18n.t('meetings.room.settings.audio_input')}
                  </Text.Normal>
                  <MediaDeviceSelect
                    onChange={handleAudioInputChange}
                    options={audioInputDevices.map((device) => ({
                      label:
                        device.label ||
                        `${i18n.t(
                          'meetings.room.control_bar.microphone.label'
                        )} ${device.deviceId.substring(0, 5)}...`,
                      value: device.deviceId,
                    }))}
                    placeholder={i18n.t('meetings.room.control_bar.microphone.placeholder')}
                    searchBoxPlaceholder={i18n.t(
                      'meetings.room.control_bar.microphone.search_placeholder'
                    )}
                    searchEmptyMessage={i18n.t(
                      'meetings.room.control_bar.microphone.no_microphones_found'
                    )}
                    enabled
                    value={activeAudioInputId || ''}
                    icon={Megaphone}
                  />
                </Box>

                {isAudioOutputSelectionSupported && (
                  <Box flexDirection="column" gap="s4">
                    <Text.Normal size="200" weight="medium">
                      {i18n.t('meetings.room.settings.audio_output')}
                    </Text.Normal>
                    <MediaDeviceSelect
                      onChange={handleAudioOutputChange}
                      options={audioOutputDevices.map((device) => ({
                        label:
                          device.label ||
                          `${i18n.t(
                            'meetings.room.control_bar.speakers.label'
                          )} ${device.deviceId.substring(0, 5)}...`,
                        value: device.deviceId,
                      }))}
                      placeholder={i18n.t('meetings.room.control_bar.speakers.placeholder')}
                      searchBoxPlaceholder={i18n.t(
                        'meetings.room.control_bar.speakers.search_placeholder'
                      )}
                      searchEmptyMessage={i18n.t(
                        'meetings.room.control_bar.speakers.no_speakers_found'
                      )}
                      enabled
                      value={activeAudioOutputId || ''}
                      icon={Phone}
                    />
                  </Box>
                )}

                <Box flexDirection="column" gap="s4">
                  <Text.Normal size="200" weight="medium">
                    {i18n.t('meetings.room.settings.video_input')}
                  </Text.Normal>
                  <MediaDeviceSelect
                    onChange={handleVideoInputChange}
                    options={videoInputDevices.map((device) => ({
                      label:
                        device.label ||
                        `${i18n.t(
                          'meetings.room.control_bar.camera.label'
                        )} ${device.deviceId.substring(0, 5)}...`,
                      value: device.deviceId,
                    }))}
                    placeholder={i18n.t('meetings.room.control_bar.camera.placeholder')}
                    searchBoxPlaceholder={i18n.t(
                      'meetings.room.control_bar.camera.search_placeholder'
                    )}
                    searchEmptyMessage={i18n.t('meetings.room.control_bar.camera.no_cameras_found')}
                    enabled
                    value={activeVideoInputId || ''}
                    icon={Video}
                  />
                </Box>
              </>
            )}

            {activeTab === 'effects' && (
              <>
                <Box flexDirection="column" gap="s4" flexGrow>
                  <Text.Normal size="200" weight="medium">
                    {i18n.t('meetings.room.settings.interactive_sounds.label')}
                  </Text.Normal>
                  <MediaDeviceSelect
                    onChange={handleSoundsChange}
                    options={[
                      {
                        label: i18n.t('meetings.room.settings.interactive_sounds.enabled'),
                        value: 'true',
                      },
                      {
                        label: i18n.t('meetings.room.settings.interactive_sounds.disabled'),
                        value: 'false',
                      },
                    ]}
                    placeholder={i18n.t('meetings.room.settings.interactive_sounds.placeholder')}
                    searchBoxPlaceholder={i18n.t(
                      'meetings.room.settings.interactive_sounds.search_placeholder'
                    )}
                    searchEmptyMessage={i18n.t(
                      'meetings.room.settings.interactive_sounds.no_sounds_found'
                    )}
                    enabled
                    value={soundsEnabled.toString()}
                    icon={Bell}
                  />
                </Box>

                {backgroundSupported && (
                  <Box flexDirection="column" gap="s4" flexGrow>
                    <Text.Normal size="200" weight="medium">
                      {i18n.t('meetings.room.settings.blur_background.label')}
                    </Text.Normal>
                    <MediaDeviceSelect
                      onChange={(value) => handleBackgroundChange(value)}
                      options={getVirtualBackgroundOptions()}
                      placeholder={i18n.t('meetings.room.settings.blur_background.placeholder')}
                      searchBoxPlaceholder={i18n.t(
                        'meetings.room.settings.blur_background.search_placeholder'
                      )}
                      searchEmptyMessage={i18n.t(
                        'meetings.room.settings.blur_background.no_backgrounds_found'
                      )}
                      enabled
                      value={backgroundType || VIRTUAL_BACKGROUND_TYPES.DISABLED}
                      icon={Image}
                    />
                  </Box>
                )}
              </>
            )}
          </AutoGrid>
        </Box>
      </OneModal.Content>
    </OneModal>
  )
}
